import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Meter, Legend } from './viz';
import { TitleIcon } from './icons';
import { pbAuthedFetch } from '../../authHeaders';

// Soft-delete recovery. GET /api/admin/trash -> { projects, items }.
// Restore and purge actions with confirm-before-purge pattern.

const PAGE = 50;

interface TrashRow {
  kind: 'project' | 'collection';
  id: number | string;
  collection?: string;
  label: string;
  sub: string;
  deleted_at: string;
}

function keyOf(row: TrashRow): string {
  return row.kind === 'project' ? `project:${row.id}` : `collection:${row.collection}:${row.id}`;
}

function dayOf(at: string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? String(at || '') : d.toISOString().slice(0, 10);
}

function ageDays(at: string): number | null {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export default function Trash() {
  const [rows, setRows] = useState<TrashRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState<Set<string>>(() => new Set());
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [shown, setShown] = useState(PAGE);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [restoreTo, setRestoreTo] = useState<string | null>(null);

  useEffect(() => {
    if (!restoreTo) return;
    const el = triggerRefs.current[restoreTo];
    if (el) el.focus();
    setRestoreTo(null);
  }, [restoreTo]);

  function cancelConfirm(k: string) {
    setConfirmId(null);
    setRestoreTo(k);
  }

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    pbAuthedFetch('/api/admin/trash')
      .then((res) => res.json().catch(() => ({})).then((d) => ({ res, d })))
      .then(({ res, d }: { res: Response; d: any }) => {
        if (!res.ok) throw new Error(d.error || `request failed (${res.status})`);
        const projects = (d.projects || []).map((p: any) => ({
          kind: 'project' as const, id: p.id, label: p.name || p.id, sub: p.id, deleted_at: p.deleted_at,
        }));
        const items = (d.items || []).map((i: any) => ({
          kind: 'collection' as const, id: i.item_id, collection: i.collection,
          label: i.item_id, sub: i.collection, deleted_at: i.deleted_at,
        }));
        setRows([...projects, ...items]);
        setShown(PAGE);
      })
      .catch((e) => setError(e.message))
      .then(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!confirmId) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancelConfirm(confirmId); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmId]);

  function setBusyFor(k: string, on: boolean) {
    setBusy((prev) => { const n = new Set(prev); if (on) n.add(k); else n.delete(k); return n; });
  }

  function bodyFor(row: TrashRow) {
    return row.kind === 'project'
      ? { kind: 'project', id: row.id }
      : { kind: 'collection', collection: row.collection, id: row.id };
  }

  function act(row: TrashRow, path: string) {
    const k = keyOf(row);
    if (busy.has(k)) return;
    setActionError('');
    setBusyFor(k, true);
    pbAuthedFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyFor(row)),
    })
      .then((res) => res.json().catch(() => ({})).then((d) => ({ res, d })))
      .then(({ res, d }: { res: Response; d: any }) => {
        if (!res.ok) throw new Error(d.error || `request failed (${res.status})`);
        setConfirmId(null);
        load();
      })
      .catch((e) => setActionError(e.message))
      .then(() => setBusyFor(k, false));
  }

  function purgeExpired() {
    const k = 'purge-expired';
    if (busy.has(k)) return;
    setActionError('');
    setBusyFor(k, true);
    pbAuthedFetch('/api/admin/trash/purge-expired', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ olderThanDays: days }),
    })
      .then((res) => res.json().catch(() => ({})).then((d) => ({ res, d })))
      .then(({ res, d }: { res: Response; d: any }) => {
        if (!res.ok) throw new Error(d.error || `request failed (${res.status})`);
        setConfirmId(null);
        load();
      })
      .catch((e) => setActionError(e.message))
      .then(() => setBusyFor(k, false));
  }

  const oldest = rows.reduce((acc, r) => {
    const a = ageDays(r.deleted_at);
    return a != null && a > acc ? a : acc;
  }, 0);
  const expiring = rows.filter((r) => {
    const a = ageDays(r.deleted_at);
    return a != null && a >= days;
  }).length;

  return (
    <section aria-label="Deleted items">
      <div className="stg-verdict">
        <div className="stg-verdict-h">
          <span className="stg-verdict-v">
            {loading ? 'Checking…' : error ? 'Unavailable' : `${rows.length} item${rows.length === 1 ? '' : 's'} recoverable`}
          </span>
          {!loading && !error && rows.length > 0 ? (
            <span className="stg-verdict-t">
              oldest deleted {oldest} day{oldest === 1 ? '' : 's'} ago · {expiring} past the {days}-day retention cutoff
            </span>
          ) : null}
        </div>
        {!loading && !error && rows.length > 0 ? (
          <div className="stg-verdict-g">
            <Meter
              label="Retention exposure"
              total={rows.length}
              segments={[
                { key: 'keep', name: `Within the ${days}-day retention`, value: rows.length - expiring, tone: 'steel' },
                { key: 'past', name: `Past the ${days}-day cutoff`, value: expiring, tone: 'amber' },
              ]}
            />
            <Legend items={[
              { key: 'keep', name: 'Within retention', value: rows.length - expiring, tone: 'steel' },
              { key: 'past', name: 'Past cutoff', value: expiring, tone: 'amber' },
            ]} />
          </div>
        ) : null}
      </div>

      {actionError ? (
        <div className="stg-actionerr">
          <div className="state state--risk">
            <div className="state__title">That action did not complete</div>
            <div className="state__body">{actionError}</div>
          </div>
        </div>
      ) : null}

      <section className="box">
        <header className="box__head">
          <TitleIcon name="trash" />
          <div className="box__head-text"><h2 className="box__title">Deleted items</h2></div>
        </header>
        <div className={`box__body${rows.length && !loading && !error ? ' box__body--flush' : ''}`}>
          {loading ? <div className="state"><div className="state__title">Loading deleted items…</div></div> : null}
          {!loading && error ? (
            <div className="state state--risk">
              <div className="state__title">Could not load deleted items</div>
              <div className="state__body">{error}<br /><button className="btn btn--sm" onClick={load} style={{ marginTop: 8 }}>Retry</button></div>
            </div>
          ) : null}
          {!loading && !error && rows.length === 0 ? (
            <div className="state"><div className="state__title">Nothing is in the trash</div><div className="state__body">Deleted projects and collection items appear here.</div></div>
          ) : null}
          {!loading && !error && rows.length > 0 ? (
            <div className="tbl-scroll">
              <table className="tbl">
                <caption className="pb-sr-only">Deleted items — what, where from, when deleted, and actions.</caption>
                <thead>
                  <tr>
                    <th scope="col">Item</th>
                    <th scope="col">Kind</th>
                    <th scope="col">Deleted</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, shown).map((row) => {
                    const k = keyOf(row);
                    const isBusy = busy.has(k);
                    const asking = confirmId === k;
                    return (
                      <tr key={k}>
                        <td className="stg-strong">{row.label}</td>
                        <td className="stg-fig">{row.kind === 'project' ? 'Project' : `Collection · ${row.sub}`}</td>
                        <td className="stg-fig">{dayOf(row.deleted_at)}</td>
                        <td className="stg-actions-cell">
                          {asking ? (
                            <div className="stg-confirm" role="group" aria-label={`Confirm purge of ${row.label}`}>
                              <span className="stg-confirm-q">Permanently delete {row.label}?</span>
                              <button type="button" className="btn btn--danger btn--sm" disabled={isBusy} aria-label={isBusy ? `Deleting ${row.label}` : `Yes, delete ${row.label}`} onClick={() => act(row, '/api/admin/trash/purge')}>
                                {isBusy ? 'Deleting…' : 'Yes, delete'}
                              </button>
                              <button type="button" className="btn btn--sm" onClick={() => cancelConfirm(k)}>Cancel</button>
                            </div>
                          ) : (
                            <div className="stg-rowbtns">
                              <button type="button" className="btn btn--sm" disabled={isBusy} aria-label={`Restore ${row.label}`} onClick={() => act(row, '/api/admin/trash/restore')}>Restore</button>
                              <button type="button" className="btn btn--sm stg-btn-quiet-danger" disabled={isBusy} ref={(el) => { if (el) triggerRefs.current[k] = el; }} aria-label={`Purge ${row.label}`} onClick={() => { setActionError(''); setConfirmId(k); }}>Purge</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
          {!loading && !error && rows.length > shown ? (
            <div className="stg-more">
              <span className="stg-note">Showing {shown} of {rows.length}. The retention purge covers all {rows.length}.</span>
              <button type="button" className="btn btn--sm" onClick={() => setShown((n) => Math.min(n + PAGE, rows.length))}>Show {Math.min(PAGE, rows.length - shown)} more</button>
            </div>
          ) : null}
        </div>
        <div className="box__foot">Soft-deleted projects and collection items. Restore returns one to service; purge destroys it.</div>
      </section>

      <section className="box">
        <header className="box__head">
          <TitleIcon name="history" />
          <div className="box__head-text"><h2 className="box__title">Retention purge</h2></div>
        </header>
        <div className="box__body">
          <div className="stg-hint">This also destroys soft-deleted comments, attachments and dependencies, which are not listed above.</div>
          <div className="stg-row" style={{ maxWidth: 220 }}>
            <label className="stg-label" htmlFor="stg-trash-days">Older than (days)</label>
            <input className="stg-input" id="stg-trash-days" type="number" min="0" max="3650" value={days} onChange={(e) => {
              const n = Math.floor(Number(e.target.value));
              setDays(Number.isFinite(n) ? Math.min(Math.max(n, 0), 3650) : 30);
              setConfirmId(null);
            }} />
          </div>
          {confirmId === 'purge-expired' ? (
            <div className="stg-confirm" role="group" aria-label="Confirm retention purge">
              <span className="stg-confirm-q">
                Permanently delete everything soft-deleted more than {days} day{days === 1 ? '' : 's'} ago{days === 0 ? ' — that is everything in the trash' : ''}?
              </span>
              <button type="button" className="btn btn--danger btn--sm" disabled={busy.has('purge-expired')} aria-label={busy.has('purge-expired') ? 'Running the retention purge' : `Yes, delete everything older than ${days} days`} onClick={purgeExpired}>
                {busy.has('purge-expired') ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button type="button" className="btn btn--sm" onClick={() => cancelConfirm('purge-expired')}>Cancel</button>
            </div>
          ) : (
            <button type="button" className="btn btn--sm stg-btn-quiet-danger" ref={(el) => { if (el) triggerRefs.current['purge-expired'] = el; }} onClick={() => { setActionError(''); setConfirmId('purge-expired'); }}>
              Purge everything older than {days} days
            </button>
          )}
        </div>
        <div className="box__foot">Permanently removes everything soft-deleted longer ago than the cutoff.</div>
      </section>
    </section>
  );
}
